import React from 'react';
import { createScrollMapsColorMap, getScrollMapsLinearGradient } from '../utils/colormap';
import { ScrollMapsFacet } from '../utils/facetdata';
import { Bounds, PlaybackScrollElementBounds, ScrollMapDisplayOptions } from '../types';
import { createInverseScales } from '../utils/scales';
import { ScrollMapsGradient } from '../ScrollMapsGradient';
import { ScrollMapsMarkerContainer } from '../ScrollMapsMarkerContainer';
import css from './index.module.scss';
import { Pane } from '@fullstory/components';
import { ScrollMapsPageFoldMarkerContainer } from '../ScrollMapsPageFoldMarkerContainer';
import { StaticScrollMapsMarkerContainer } from '../StaticScrollMapsMarkerContainer';

const GradientZIndexOffset = 10;
const PageFoldMarkerZIndexOffset = 50;
const MarkerZIndexOffset = 80;

export interface ScrollMapsOverlayProps {
  /**
   * The current response from the network.
   */
  facetData: ScrollMapsFacet;
  /**
   * The bounds for the playback container
   */
  playbackContainerBounds: Bounds;
  /**
   * The bounds for the overlay container
   */
  overlayContainerBounds: Bounds;
  /**
   * The bounds for the page itself
   */
  pageBounds: Bounds;
  /**
   * Optional gradient bounds. If none is given, then the pageBounds will be used
   */
  gradientBoundsOverride?: Bounds;
  /**
   * Whether or not this is for an internal scroll. False means a normal document level scroll.
   */
  internalScroll?: boolean;
  /**
   * The container bounds of the internally scrolling element.
   */
  internalScrollContainerBounds?: Bounds;
  /**
   * The viewport of the internally scrolling element.
   */
  internalScrollViewportBounds?: Bounds;
  /**
   * The median viewport height of users upon first loading the page (in px).
   */
  medianPageFold: number;
  /**
   * The scale to be applied to undo viewport scaling by session Playback.
   */
  visualInverseScale: number;
  /**
   * The scale to be applied to undo the frame scaling applied by session Playback.
   */
  frameInverseScale: number;
  /**
   * The scale to be applied to undo the zoom scaling applied by session Playback.
   */
  zoomInverseScale: number;
  /**
   * Scroll map display options
   */
  scrollMapDisplayOptions: ScrollMapDisplayOptions;
}

/**
 * The Scroll Maps Overlay for displaying Scroll Maps information on top of session replay.
 */
export const ScrollMapsOverlay: React.FC<ScrollMapsOverlayProps> = ({
  facetData,
  playbackContainerBounds,
  overlayContainerBounds,
  pageBounds,
  gradientBoundsOverride = pageBounds,
  medianPageFold,
  internalScroll,
  internalScrollContainerBounds,
  internalScrollViewportBounds,
  visualInverseScale,
  frameInverseScale,
  zoomInverseScale,
  scrollMapDisplayOptions,
}) => {
  const inverseScales = createInverseScales(
    visualInverseScale,
    frameInverseScale,
    zoomInverseScale,
  );

  if (scrollMapDisplayOptions.markerScaleFactor) {
    inverseScales.y = inverseScales.y * scrollMapDisplayOptions.markerScaleFactor;
    inverseScales.x = inverseScales.x * scrollMapDisplayOptions.markerScaleFactor;
  }

  const colorMap = createScrollMapsColorMap(facetData);
  const gradientBounds = { ...gradientBoundsOverride };
  gradientBounds.zIndex = pageBounds.zIndex + GradientZIndexOffset;
  const { displayMedianPageFold, showStaticMarkers } = scrollMapDisplayOptions;

  const playbackScrollElementBounds: PlaybackScrollElementBounds = {
    scrollElementTop: internalScroll ? internalScrollContainerBounds!.top : pageBounds.top,
    viewportHeight: internalScroll
      ? internalScrollViewportBounds!.height
      : playbackContainerBounds.height,
    containerHeight: internalScroll ? internalScrollContainerBounds!.height : pageBounds.height,
  };

  return (
    <Pane data-component="ScrollMapsOverlay" data-fs="scroll-maps-overlay" className={css.overlay}>
      <ScrollMapsGradient
        gradient={getScrollMapsLinearGradient(colorMap)}
        bounds={gradientBounds}
      />
      {displayMedianPageFold && medianPageFold > 0 && (
        <ScrollMapsPageFoldMarkerContainer
          playbackContainerBounds={playbackContainerBounds}
          pageBounds={pageBounds}
          inverseScales={inverseScales}
          medianPageFold={medianPageFold}
          zIndexOffset={PageFoldMarkerZIndexOffset}
          internalScroll={internalScroll}
          internalScrollContainerBounds={internalScrollContainerBounds}
          internalScrollViewportBounds={internalScrollViewportBounds}
        />
      )}
      {showStaticMarkers ? (
        <StaticScrollMapsMarkerContainer
          colorMap={colorMap}
          facetData={facetData}
          playbackScrollElementBounds={playbackScrollElementBounds}
          pageBounds={pageBounds}
          inverseScales={inverseScales}
          zIndexOffset={MarkerZIndexOffset}
        />
      ) : (
        <ScrollMapsMarkerContainer
          colorMap={colorMap}
          facetData={facetData}
          playbackScrollElementBounds={playbackScrollElementBounds}
          overlayContainerBounds={overlayContainerBounds}
          pageBounds={pageBounds}
          inverseScales={inverseScales}
          zIndexOffset={MarkerZIndexOffset}
        />
      )}
    </Pane>
  );
};
